import React from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingHeader from '../../components/hospitalOnboarding/OnboardingHeader';
import { Check, ArrowRight, AlertTriangle, XCircle, CheckCircle, ExternalLink } from 'lucide-react';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';

// CRA (react-scripts) only injects REACT_APP_* env vars at build time.
// VITE_* variables are never available in a CRA build. Use REACT_APP_SUPPORT_PHONE
// in your .env files. The import.meta fallback is kept for any future Vite migration.
const SUPPORT_PHONE =
  (typeof process !== 'undefined' && process.env?.REACT_APP_SUPPORT_PHONE) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPPORT_PHONE) ||
  '+91 9904 104 104';

export const HospitalOnboardingSubmittedPage = () => {
  const navigate = useNavigate();
  const { formData, startOver } = useHospitalOnboarding();

  const isFleet =
    formData?.partnerType === 'fleet' ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('risa_partner_type') === 'fleet');

  const applicationCode =
    formData?.application_code ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('risa_last_submitted_application_code') : '') ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('risa_current_application_id') : '') ||
    'RISA-APP-SUBMITTED';

  const status = formData?.status || 'SUBMITTED';
  const reviewNotes = formData?.review_notes;

  const handleReturnHome = () => {
    if (startOver) {
      startOver();
    }
    navigate('/');
  };

  const handleEditApplication = () => {
    if (isFleet) {
      navigate('/hospital/onboarding/fleet/step/1');
    } else {
      navigate('/hospital/onboarding/step/1');
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col font-sans"
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
      }}
    >
      <OnboardingHeader />

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 flex items-center justify-center">
        <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-8 sm:p-12 text-center animate-fade-in w-full">
          {/* Status-specific icon circle */}
          {status === 'CHANGES_REQUESTED' ? (
            <div className="w-20 h-20 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-amber-600 stroke-[2.5]" />
            </div>
          ) : status === 'APPROVED' ? (
            <div className="w-20 h-20 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600 stroke-[2.5]" />
            </div>
          ) : status === 'REJECTED' ? (
            <div className="w-20 h-20 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600 stroke-[2.5]" />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-emerald-600 stroke-[3]" />
            </div>
          )}

          {/* Subheader Badge */}
          <span
            className={`text-xs font-semibold uppercase tracking-widest block mb-2 ${
              status === 'CHANGES_REQUESTED'
                ? 'text-amber-700'
                : status === 'APPROVED'
                ? 'text-emerald-700'
                : status === 'REJECTED'
                ? 'text-red-700'
                : 'text-blue-600'
            }`}
          >
            {status === 'CHANGES_REQUESTED'
              ? 'ACTION REQUIRED: CHANGES REQUESTED'
              : status === 'APPROVED'
              ? 'PARTNER ONBOARDING APPROVED'
              : status === 'REJECTED'
              ? 'APPLICATION NOT APPROVED'
              : 'ONBOARDING REQUEST SUBMITTED'}
          </span>

          {/* Main Title */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-800 tracking-tight mb-3">
            {status === 'CHANGES_REQUESTED'
              ? 'Please Update Your Application'
              : status === 'APPROVED'
              ? 'Welcome to RISA Network!'
              : status === 'REJECTED'
              ? 'Application Status Update'
              : 'Thank you for joining us.'}
          </h1>

          {/* Subtitle */}
          <p className="text-xs sm:text-sm text-slate-500 max-w-md mx-auto mb-4 leading-relaxed">
            {status === 'CHANGES_REQUESTED'
              ? 'The reviewer reviewed your application and requested some adjustments. Please see notes below and update your submission.'
              : status === 'APPROVED'
              ? 'Your onboarding application has been verified and approved. Your admin account is now active.'
              : status === 'REJECTED'
              ? 'Your application could not be approved at this time. Please see details below or contact our support team.'
              : 'Thank you for submitting your details to RISA Smart Ambulance. Our team will review your information and reach out to guide you through the next steps.'}
          </p>

          {/* Review Notes Alert (B7) */}
          {status === 'CHANGES_REQUESTED' && reviewNotes && (
            <div className="my-5 p-4 rounded-2xl bg-amber-50/80 border border-amber-300/80 text-left space-y-1.5 shadow-2xs">
              <div className="flex items-center gap-1.5 text-amber-800 font-semibold text-xs">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>Super Admin Reviewer Notes:</span>
              </div>
              <p className="text-xs text-amber-900 whitespace-pre-wrap font-medium pl-5">
                {reviewNotes}
              </p>
            </div>
          )}

          {/* Application Code Card */}
          <div className="my-6 p-4 rounded-2xl bg-blue-50/70 border border-blue-200/80 max-w-sm mx-auto flex flex-col items-center justify-center">
            <span className="text-[11px] font-medium text-blue-700 uppercase tracking-wider mb-1">
              Application Reference Code
            </span>
            <div className="font-mono text-lg sm:text-xl font-semibold text-blue-800 select-all tracking-wider">
              {applicationCode}
            </div>
            <p className="text-[10px] text-blue-600/80 mt-1">Please quote this code for status inquiries</p>
          </div>

          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-xs font-medium mb-8 shadow-2xs">
            <span>
              {status === 'CHANGES_REQUESTED'
                ? 'Status: Changes Requested by Super Admin'
                : status === 'APPROVED'
                ? 'Status: Approved & Live Access Granted'
                : status === 'REJECTED'
                ? 'Status: Rejected'
                : 'Status: Submitted - Pending Super Admin Review'}
            </span>
          </div>

          {/* Support Helpline */}
          <div className="mb-6">
            <p className="text-xs sm:text-sm text-slate-600 font-medium mb-1.5">
              For any queries or questions, please call
            </p>
            <a
              href={`tel:${SUPPORT_PHONE.replace(/\s+/g, '')}`}
              className="font-semibold text-2xl sm:text-3xl text-blue-600 hover:text-blue-700 transition-colors block"
            >
              {SUPPORT_PHONE}
            </a>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {status === 'CHANGES_REQUESTED' && (
              <button
                type="button"
                onClick={handleEditApplication}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs sm:text-sm font-semibold shadow-xs transition-colors cursor-pointer"
              >
                <span>Edit & Resubmit Application</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {status === 'APPROVED' && (
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold shadow-xs transition-colors cursor-pointer"
              >
                <span>Go to Partner Login</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={handleReturnHome}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs sm:text-sm font-medium shadow-xs transition-colors cursor-pointer"
            >
              <span>Return to Home</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="py-5 text-center text-xs text-slate-400 border-t border-slate-200/80 bg-white">
        © {new Date().getFullYear()} RISA Smart Ambulance Platform. All rights reserved.
      </footer>
    </div>
  );
};

export default HospitalOnboardingSubmittedPage;
